import SEO from '../components/SEO.jsx';
import { FaCalendarAlt, FaUser, FaArrowRight, FaTag, FaClock, FaComments, FaShare, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts.jsx';

const Blog = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedAuthor, setSelectedAuthor] = useState(null);

  // Author images mapping
  const authorImages = {
    'Arjun Sharma': '/team-1.png',
    'Priya Patel': '/team-2.png',
    'Vikram Singh': '/team-3.png',
    'Ananya Gupta': '/team-4.png',
    'David Wilson': '/team-5.png',
    'Lisa Anderson': '/team-6.png'
  };

  const blogStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Sutrasync Blog - Technology & Innovation Insights",
    "description": "Expert insights on web development, mobile apps, AI, cloud computing, cybersecurity, and digital transformation. Stay ahead with our comprehensive tech guides and industry analysis.",
    "url": "https://sutrasync.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Sutrasync",
      "logo": {
        "@type": "ImageObject",
        "url": "https://sutrasync.com/logo.png"
      }
    },
    "blogPost": []
  };

  // Card Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.6,
        ease: "easeOut"
      }
    })
  };

  const imageVariants = {
    rest: { scale: 1, filter: "grayscale(100%)" },
    hover: { 
      scale: 1.15, 
      filter: "grayscale(0%)",
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  // 3D Tilt effect on mouse move
  const handleMouseMove = (e, cardId) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / 15;
    const rotateY = (centerX - x) / 15;
    
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;
  };

  const handleMouseLeave = (e) => {
    const card = e.currentTarget;
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
  };

  return (
    <>
      <SEO 
        title="Tech Blog - Web Development, App Development & Digital Marketing Insights"
        description="Expert insights on web development, mobile apps, digital marketing & technology trends from Bangalore's leading IT company. Practical guides, tutorials, case studies & industry best practices from Sutrasync experts."
        keywords="Sutrasync blog, web development blog, app development insights, digital marketing tips, technology blog Bangalore, IT industry news, web design tutorials, mobile app development guide, SEO best practices, programming tutorials, software development insights"
        url="https://sutrasync.com/blog"
        image="https://sutrasync.com/Web development.png"
        structuredData={blogStructuredData}
      />

      {/* Author Modal */}
      <AnimatePresence>
        {selectedAuthor && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedAuthor(null)}
          >
            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            
            {/* Modal Content */}
            <motion.div
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedAuthor(null)}
                className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110"
              >
                <FaTimes className="text-xl" />
              </button>

              {/* Author Image */}
              <div className="relative h-80 bg-gradient-to-br from-[#22446b] to-[#338edc] overflow-hidden">
                <img
                  src={authorImages[selectedAuthor]}
                  alt={selectedAuthor}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>

              {/* Author Info */}
              <div className="p-8">
                <h3 className="text-3xl font-rajdhani font-bold text-[#22446b] mb-2">
                  {selectedAuthor}
                </h3>
                <p className="text-gray-600 font-opensans mb-4">
                  Content Writer & Tech Enthusiast
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <FaUser />
                  <span>Author at Sutrasync</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="pt-16 md:pt-20 min-h-screen bg-white">
        {/* Enhanced Header Section with Motion Effects */}
        <section className="relative py-24 lg:py-32 bg-[#338edc] text-white overflow-hidden min-h-[600px] flex items-center">
          {/* Animated Background Image */}
          <motion.div 
            className="absolute inset-0"
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          >
            <img 
              src="/Web development.png" 
              alt="Blog Background" 
              className="w-full h-full object-cover opacity-20"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#22446b] via-[#2d5a8a] to-[#338edc]"></div>
          </motion.div>

          {/* Animated Geometric Pattern Overlay */}
          <motion.div 
            className="absolute inset-0 opacity-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.1 }}
            transition={{ duration: 2, delay: 0.5 }}
          >
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="blog-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                  <motion.path 
                    d="M0 0 L100 100" 
                    stroke="white" 
                    strokeWidth="1" 
                    opacity="0.5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, delay: 0.8 }}
                  />
                  <motion.path 
                    d="M100 0 L0 100" 
                    stroke="white" 
                    strokeWidth="1" 
                    opacity="0.5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, delay: 1 }}
                  />
                  <motion.circle 
                    cx="50" 
                    cy="50" 
                    r="3" 
                    fill="white" 
                    opacity="0.6"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 1.2 }}
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#blog-pattern)" />
            </svg>
          </motion.div>

          {/* Floating Particles Effect */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
             
          {/* Content Container */}
          <div className="container mx-auto px-6 lg:px-8 relative z-10 text-center">
            {/* Animated Badge */}
            <motion.div
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-2 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="w-2 h-2 bg-[#22446b] rounded-full animate-pulse"></span>
              <span className="text-sm font-semibold text-white">Latest Tech Insights & Trends</span>
            </motion.div>

            {/* Main Title with Motion */}
            <motion.h1 
              className="text-5xl lg:text-7xl xl:text-8xl font-rajdhani font-bold mb-6 tracking-tight"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            >
              <span className="text-white">Tech </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-400 to-gray-600">Insights</span>
            </motion.h1>

            {/* Subtitle with Motion */}
            <motion.p 
              className="text-xl lg:text-2xl text-white/80 max-w-3xl mx-auto font-opensans leading-relaxed mb-10"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
            >
              Exploring the frontiers of technology, design, and digital innovation. 
              Stay ahead with expert analysis, tutorials, and industry trends.
            </motion.p>

            {/* CTA Buttons with Motion */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.button
                onClick={() => document.getElementById('blog-posts').scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-[#22446b] font-rajdhani font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore Articles
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>

            {/* Stats Bar */}
            <motion.div
              className="mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-rajdhani font-bold text-white mb-2">50+</div>
                <div className="text-sm text-white/70">Articles</div>
              </div>
              <div className="text-center border-x border-white/20">
                <div className="text-3xl lg:text-4xl font-rajdhani font-bold text-white mb-2">10K+</div>
                <div className="text-sm text-white/70">Readers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-rajdhani font-bold text-white mb-2">Weekly</div>
                <div className="text-sm text-white/70">Updates</div>
              </div>
            </motion.div>
          </div>

          {/* Animated Bottom Wave */}
          <motion.div 
            className="absolute bottom-0 left-0 right-0"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
          >
            <svg className="w-full h-20" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M0,60 C300,120 900,0 1200,60 L1200,120 L0,120 Z" fill="white" opacity="0.1"/>
            </svg>
          </motion.div>
        </section>

        {/* Blog Grid */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50" id="blog-posts">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <motion.article 
                  key={post.id}
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={cardVariants}
                  onMouseEnter={() => setHoveredCard(post.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className="relative group bg-white rounded-2xl flex flex-col h-full overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl"
                  style={{
                    boxShadow: hoveredCard === post.id 
                      ? '0 20px 40px rgba(0,0,0,0.15)' 
                      : '0 4px 20px rgba(0,0,0,0.08)'
                  }}
                >
                  {/* Shine Effect Overlay */}
                  <div 
                    className="absolute inset-0 pointer-events-none z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
                      transform: 'translateX(-100%)',
                      animation: hoveredCard === post.id ? 'shine 1.5s ease-in-out' : 'none'
                    }}
                  />
                  
                  {/* Image Area */}
                  <Link to={`/blog/${post.slug}`} className="block relative rounded-t-2xl overflow-hidden">
                    <motion.div className="h-64 bg-gray-100 relative overflow-hidden">
                      <motion.img 
                        src={post.image} 
                        alt={post.title} 
                        className="w-full h-full object-cover"
                        animate={{
                          scale: hoveredCard === post.id ? 1.08 : 1
                        }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      {/* Category Badge */}
                      <motion.div 
                        className="absolute top-4 left-4 z-20"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                      >
                        <span className="bg-gradient-to-r from-[#22446b] to-[#338edc] text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wider shadow-lg">
                          {post.category}
                        </span>
                      </motion.div>

                      {/* Read Time Badge */}
                      <motion.div 
                        className="absolute bottom-4 right-4 z-20"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                      >
                        <span className="bg-white/95 backdrop-blur-sm text-[#22446b] text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-2 shadow-lg">
                          <FaClock /> {post.readTime}
                        </span>
                      </motion.div>

                      {/* Floating "Read More" Button */}
                      <motion.div 
                        className="absolute inset-0 flex items-center justify-center z-20"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ 
                          opacity: hoveredCard === post.id ? 1 : 0,
                          scale: hoveredCard === post.id ? 1 : 0.8
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="bg-gradient-to-r from-[#22446b] to-[#338edc] text-white font-bold px-8 py-4 rounded-full flex items-center gap-3 shadow-2xl">
                          Read Article <FaArrowRight />
                        </div>
                      </motion.div>
                    </motion.div>
                  </Link>

                  {/* Content */}
                  <div className="p-8 flex-grow flex flex-col bg-white relative">
                    <motion.div 
                      className="flex items-center text-xs text-gray-500 gap-4 mb-4 font-opensans"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                    >
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt />
                        {post.date}
                      </div>
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:text-[#338edc] transition-colors duration-300"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedAuthor(post.author);
                        }}
                      >
                        <FaUser />
                        {post.author}
                      </div>
                    </motion.div>

                    <Link to={`/blog/${post.slug}`}>
                      <motion.h2 
                        className="text-2xl font-bold font-rajdhani text-[#22446b] mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#22446b] group-hover:to-[#338edc] transition-all leading-tight"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                      >
                        {post.title}
                      </motion.h2>
                    </Link>

                    <motion.p 
                      className="text-gray-600 mb-6 font-opensans leading-relaxed flex-grow line-clamp-3"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                    >
                      {post.excerpt}
                    </motion.p>

                    {/* Tags */}
                    <motion.div 
                      className="flex flex-wrap gap-2 mb-6"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                    >
                      {post.tags.slice(0, 3).map((tag, i) => (
                        <motion.span 
                          key={i}
                          className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full font-semibold hover:bg-gradient-to-r hover:from-[#22446b] hover:to-[#338edc] hover:text-white transition-all duration-300 cursor-pointer"
                          whileHover={{ 
                            scale: 1.05,
                            y: -2
                          }}
                        >
                          {tag}
                        </motion.span>
                      ))}
                    </motion.div>

                    {/* Bottom Action Bar */}
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center justify-between text-transparent bg-clip-text bg-gradient-to-r from-[#22446b] to-[#338edc] font-bold uppercase tracking-widest text-sm group/link hover:from-[#338edc] hover:to-[#22446b] transition-all mt-auto pt-4 border-t border-gray-100"
                    >
                      <span>Continue Reading</span>
                      <motion.span
                        animate={{ x: hoveredCard === post.id ? [0, 5, 0] : 0 }}
                        transition={{ repeat: hoveredCard === post.id ? Infinity : 0, duration: 1.5 }}
                      >
                        <FaArrowRight />
                      </motion.span>
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;
